import i18n from "i18next";

import { useAttributesContext } from "shared/contexts/AttributesContext";
import { ContextProviderName } from "shared/contexts/types";
import {
  TRANSPORT_CATEGORY_FILTER,
  VEHICLE_TAGS_GENERIC_FILTER,
  VEHICLES_GENERIC_FILTER,
} from "shared/filterDefinitions";
import { DataType } from "shared/types";

import { FilterType } from "features/ui/Filters/types";

import useDealersSchema from "./dealerSchema";
import { SchemaEntryOverride, SchemaRelations, UseSchema } from "./types";
import { getGroupBySelectOptions } from "./utils/group-by-utils";
import {
  getAttributeMap,
  getDisplayLabelFunction,
  getFilterFunction,
  getSchemaEntryFunction,
  getSchemaForAccessorFunction,
  getSchemaFromAttributes,
} from "./utils/schema-utils";
import useVehicleECUsSchema from "./vehicleECUsSchema";
import useVehicleOptionsSchema from "./vehicleOptionsSchema";

const VALUES_ENDPOINT = "vehicles";

const useVehiclesSchema = (accessorsToHide: string[] = []): UseSchema => {
  const t = i18n.t;
  const { groupByValues, attributes } = useAttributesContext(
    ContextProviderName.Vehicle
  );

  const attributeMap = getAttributeMap(attributes);

  const getDisplayLabel = getDisplayLabelFunction(attributeMap);

  const dealerSchema = useDealersSchema();
  const ecuSchema = useVehicleECUsSchema();
  const optionsSchema = useVehicleOptionsSchema();

  const getFilter = getFilterFunction(
    VEHICLES_GENERIC_FILTER,
    attributeMap,
    VALUES_ENDPOINT
  );

  const getSchemaEntry = getSchemaEntryFunction(getFilter, attributeMap);

  const relations: SchemaRelations = {
    "/api/v1/dealers": dealerSchema,
  };

  const overrides: SchemaEntryOverride = {
    tags: {
      filterOverride: VEHICLE_TAGS_GENERIC_FILTER({
        label: getDisplayLabel("tags", t("pages:vehicles.tags")),
        fieldName: "tags",
        filterType: FilterType.STRING,
        fieldNameForAPI: "ID",
        disableFiltering: true,
        search: true,
      }),
    },
    predictions: {
      ignore: true,
    },
    transportCategoryID: {
      filterOverride: TRANSPORT_CATEGORY_FILTER(t),
    },
    vehicleModelYear: {
      schemaOverride: {
        dataType: DataType.STRING,
      },
    },
    vehiclePlanningModelYear: {
      schemaOverride: {
        dataType: DataType.STRING,
      },
    },
    engineModelYear: {
      schemaOverride: {
        dataType: DataType.STRING,
      },
    },
  };

  const schema = getSchemaFromAttributes(
    attributes,
    getSchemaEntry,
    overrides,
    relations,
    accessorsToHide
  );

  const getSchemaForAccessor = getSchemaForAccessorFunction(schema);

  const groupByRelations: SchemaRelations = {
    "/api/v1/dealers": dealerSchema,
    "/api/v1/vehicleECUs": ecuSchema,
    "/api/v1/vehicleOptions": optionsSchema,
  };

  const attributesForGroupBy = attributes?.filter(
    ({ ID }) => !accessorsToHide.includes(ID)
  );

  const groupBySelectOptions = getGroupBySelectOptions(
    attributesForGroupBy,
    groupByRelations,
    groupByValues
  );

  return {
    schema,
    getDisplayLabel,
    attributeMap,
    attributes,
    groupBySelectOptions,
    attributeAccessors: schema.map(
      ({ accessor, filter }) => filter?.fieldName || accessor
    ),
    getSchemaForAccessor,
    getSchemaEntry,
  };
};

export default useVehiclesSchema;
